import { useEffect, useRef } from 'react';
import { useDispatch } from 'react-redux';
/* eslint-disable import/no-extraneous-dependencies */
import AdyenCheckout from '@adyen/adyen-web';
import UIElement from '@adyen/adyen-web/dist/types/components/UIElement';
import { OnPaymentCompletedData } from '@adyen/adyen-web/dist/types/components/types';
import AdyenCheckoutError from '@adyen/adyen-web/dist/types/core/Errors/AdyenCheckoutError';
import Checkout from '@adyen/adyen-web/dist/types/core';
import { CoreOptions } from '@adyen/adyen-web/dist/types/core/types';
/* eslint-enable */
import Routes from '../../constants/routes';
import { changeFormData, changeState, validateField, } from './reducers';
import axios from '../../utils/axios';
import {
    ADYEN_CLIENT_KEY,
    ADYEN_ENVIRONMENT,
    AVAILABLE_PAYMENT_METHODS,
    PAYMENT_METHOD_KEYS,
    PaymentOptionsDataType
} from './payment.constants';
import { useAppSelector } from '../../store';
import API_URLS from '../../constants/api-urls';
import serializeQueryParams from '../../utils/url/serialize-query-params';
import { AVAILABLE_WORLDS } from '../../languages/constants';
import { world } from '../../languages';
import { PaymentOptionType } from '../../utils/enhanced-ecommerce';
import { FormDataType } from './components/purchase-form/purchase-form.constants';
import { KUSHKI_AVAILABLE_REGION } from './components/kushki/constants/kushki';

export const useAutocompleteInvoice = (user: User) => {
    const dispatch = useDispatch();
    const geotargeting = useAppSelector((state) => state.main.geotargeting);
    const isAlreadyCompleted = useRef<boolean>(false);

    return useEffect(() => {
        if (!isAlreadyCompleted.current) {
            const isInvoiceAutocomplete =
                window.location.pathname === Routes.PAYMENT.path &&
                user?.profileDetails?.invoiceDetails?.address?.firstName;

            if (isInvoiceAutocomplete) {
                isAlreadyCompleted.current = true;
                dispatch(changeState({ withInvoice: true }));
                dispatch(changeFormData(user.profileDetails.invoiceDetails.address as unknown as FormDataType));
            } else if (geotargeting?.countryCode) {
                dispatch(changeFormData({ country: geotargeting.countryCode }));
            }
        }
    }, [user, geotargeting.countryCode]);
};

export const useCheckEmail = () => {
    const dispatch = useDispatch();

    return () => {
        dispatch(changeState({ isSchoolMaster: false }));
        dispatch(validateField('email'));
    };
};

export const resolvePreselectedPaymentOption = (paymentOptionsData: PaymentOptionsDataType, haveRecentlyUsedPaymentMethod = false): PaymentOptionType | null => {
    const queryParams = serializeQueryParams();
    const allowedPaymentMethods = paymentOptionsData?.allowedPaymentMethods || [];

    // Preselect PP payment method if user was redirected to the Checkout page after select PP Express on the Cart page
    if (queryParams.ppTransactionId) {
        return AVAILABLE_PAYMENT_METHODS[PAYMENT_METHOD_KEYS.PAYPAL_CHECKOUT];
    }

    if (
        queryParams?.defaultPaymentMethod
        && allowedPaymentMethods.includes(queryParams.defaultPaymentMethod as string)
        && AVAILABLE_PAYMENT_METHODS[queryParams.defaultPaymentMethod as string]
    ) {
        return AVAILABLE_PAYMENT_METHODS[queryParams.defaultPaymentMethod as string] ?? null;
    }

    if (haveRecentlyUsedPaymentMethod && paymentOptionsData.recentlyUsedPaymentMethod) {
        return AVAILABLE_PAYMENT_METHODS[paymentOptionsData.recentlyUsedPaymentMethod] ?? null;
    }

    return null;
};

type AdyenSessionData = {
    amount: {
        currency: string;
        value: number;
    };
    session?: {
        id: string;
        data: string;
    }
};

export type AdyeConfig = {
    configuration: { merchantId: string; merchantName?: string; gatewayMerchantId?: string };
    name: string;
    type: string;
};

export const createAdyenCheckout = (
    sessionData: AdyenSessionData,
    onSubmit?: (state: never, component: UIElement) => void,
    onCompleted?: (result: OnPaymentCompletedData) => void,
    onAdditionalDetails?: (state: never, component: UIElement) => void,
    onError?: (error?: AdyenCheckoutError) => void
): Promise<Checkout> => {
    const configuration: CoreOptions = {
        environment: ADYEN_ENVIRONMENT,
        clientKey: ADYEN_CLIENT_KEY,
        session: {
            id: sessionData?.session?.id,
            sessionData: sessionData?.session?.data
        },
        countryCode: getCountryCode(),
        locale: `${world}-${getCountryCode()}`,
        amount: sessionData?.amount,
        onSubmit,
        onAdditionalDetails,
        onPaymentCompleted: onCompleted,
        onError
    };

    return AdyenCheckout(configuration);
};

export const isAdyenPaymentMethodAvailable = async (
    adyenCheckout: Checkout,
    method: 'googlepay' | 'applepay'
): Promise<boolean> => {
    try {
        return (await adyenCheckout.create(method).isAvailable());
    } catch (e) {
        console.warn(e);

        return false;
    }
};

export const getAdyenSessionData = async (
    method = PAYMENT_METHOD_KEYS.GOOGLE_PAY,
    userEmail = '',
    isInitRequest = true
): Promise<AdyenSessionData> => {
    const search = serializeQueryParams();
    const requestConfig: {[key: string]: string | boolean} = {};

    if (isInitRequest === false) {
        requestConfig.init = false;
    }

    if (search.region) {
        requestConfig.countryCode = <string>search.region;
    }

    if (userEmail) {
        requestConfig.email = userEmail;
    }

    if (search.materialId) {
        requestConfig.materialId = <string>search.materialId;
    }

    return axios.get(`${API_URLS.PAYMENT_OPTIONS}/${method}`, {
        params: { ...requestConfig }
    });
};

export const checkAvailableAdyenAppleAndGoogleMethods = async (methods: PAYMENT_METHOD_KEYS[]) => {
    const paymentMethodsAvailability = {
        [PAYMENT_METHOD_KEYS.GOOGLE_PAY]: false,
        [PAYMENT_METHOD_KEYS.APPLE_PAY]: false,
    };

    try {
        const sessionDataRequest = await getAdyenSessionData();

        if (!sessionDataRequest?.session) {
            return paymentMethodsAvailability;
        }

        const adyenCheckout = await createAdyenCheckout(sessionDataRequest);

        paymentMethodsAvailability[PAYMENT_METHOD_KEYS.GOOGLE_PAY] = methods.includes(PAYMENT_METHOD_KEYS.GOOGLE_PAY) && (await isAdyenPaymentMethodAvailable(adyenCheckout, 'googlepay'));
        paymentMethodsAvailability[PAYMENT_METHOD_KEYS.APPLE_PAY] = methods.includes(PAYMENT_METHOD_KEYS.APPLE_PAY) && (await isAdyenPaymentMethodAvailable(adyenCheckout, 'applepay'));
    } catch (error) {}

    return paymentMethodsAvailability;
};

export const isTest = () => {
    const queryParams = serializeQueryParams();

    return Boolean(queryParams.region);
};

export const isKushkiPayment = (paymentOptionsData: PaymentOptionsDataType) => {
    return (
        KUSHKI_AVAILABLE_REGION.includes(paymentOptionsData.userCountry) &&
        world === AVAILABLE_WORLDS.ES &&
        paymentOptionsData.kushkiEnv.merchantId !== null
    );
};

export const getActivePaymentMethodKey = (activeMethodId: string) => {
    return Object.keys(AVAILABLE_PAYMENT_METHODS)
            .find((key) => AVAILABLE_PAYMENT_METHODS[key]?.id === activeMethodId)
        || activeMethodId
};

function getCountryCode(): string {
    let countryCode = world;

    // If language abbreviation is different from country code abbreviation
    // Example HI - hindi language, but country code is IN - India
    if (countryCode === AVAILABLE_WORLDS.HI) {
        countryCode = 'IN';
    }

    return countryCode.toUpperCase();
};
